import { isInBrowser } from '../../common/utils/ssrUtils.js';
import {
  CHAT_OPENED_DATA_LAYER_EVENTS,
  GENESYS_FALLBACK_AGENT_NAME,
  MAX_CUSTOM_ATTRIBUTES_SIZE_BYTES,
} from './constants.js';
import { SALES_PATH_PREFIXES } from './salesPaths.js';
import { GenesysDeviceType, GenesysService } from './types.js';
import type { GenesysCustomAttributes, GenesysMessagePayload, WindowWithDataLayer } from './types.js';
import type { ChatScheduleResponse } from '../../generated/api/chatScheduleResponse.js';
import type { SiteStructureNode } from '../../generated/api/siteStructureNode.js';

/**
 * Show or hide the entire Genesys chat widget (icon and window).
 * Custom widget uses #elisa-chatbot-host, legacy Genesys SDK uses #genesys-messenger.
 */
export const toggleGenesysWidgetVisibility = (visible: boolean): void => {
  if (!isInBrowser()) {
    return;
  }
  const container = document.getElementById('elisa-chatbot-host') || document.getElementById('genesys-messenger');
  if (container) {
    container.style.visibility = visible ? '' : 'hidden';
  }
};

export const getCustomAttributesSize = (attributes: Partial<GenesysCustomAttributes>): number => {
  try {
    return JSON.stringify(attributes).length;
  } catch {
    return 0;
  }
};

export const trimOversizedAttributes = (
  attributes: Partial<GenesysCustomAttributes>
): Partial<GenesysCustomAttributes> => {
  const size = getCustomAttributesSize(attributes);
  if (size > MAX_CUSTOM_ATTRIBUTES_SIZE_BYTES && attributes.shoppingCart) {
    const { shoppingCart: _, ...rest } = attributes;
    return rest;
  }
  return attributes;
};

export const detectService = (pathname: string, siteStructureNodes?: Array<SiteStructureNode>): GenesysService => {
  const decodedPathname = decodeURIComponent(pathname);
  if (decodedPathname.startsWith('/työntekijä') || decodedPathname.startsWith('/tyontekija')) {
    return GenesysService.EOE;
  }

  if (decodedPathname.startsWith('/omaelisa')) {
    return GenesysService.NOE;
  }

  if (siteStructureNodes?.some(node => node.path === '/kauppa')) {
    return GenesysService.SALES;
  }

  for (const prefix of SALES_PATH_PREFIXES) {
    if (decodedPathname.startsWith(prefix)) {
      return GenesysService.SALES;
    }
  }

  return GenesysService.OTHER;
};

export const detectDeviceType = (): GenesysDeviceType => {
  if (!isInBrowser()) {
    return GenesysDeviceType.DESKTOP;
  }

  const width = window.innerWidth;

  if (width < 768) {
    return GenesysDeviceType.MOBILE;
  } else if (width < 1024) {
    return GenesysDeviceType.TABLET;
  } else {
    return GenesysDeviceType.DESKTOP;
  }
};

export const isChatScheduleOpen = (schedule: ChatScheduleResponse, now: Date = new Date()): boolean => {
  if (!schedule.isWorkingDay || !schedule.from || !schedule.until) {
    return false;
  }

  const nowMs = now.getTime();
  const fromMs = new Date(schedule.from).getTime();
  const untilMs = new Date(schedule.until).getTime();

  if (nowMs < fromMs || nowMs >= untilMs) {
    return false;
  }

  const isWithinClosedInterval = schedule.closed.some(interval => {
    const closedFromMs = new Date(interval.from).getTime();
    const closedUntilMs = new Date(interval.until).getTime();
    return nowMs >= closedFromMs && nowMs < closedUntilMs;
  });

  return !isWithinClosedInterval;
};

export const hasChatBeenOpened = (): boolean => {
  if (!isInBrowser()) {
    return false;
  }

  const dataLayer = (window as WindowWithDataLayer).dataLayer;
  if (!Array.isArray(dataLayer)) {
    return false;
  }

  return dataLayer.some(entry => CHAT_OPENED_DATA_LAYER_EVENTS.includes(String(entry?.event_name)));
};

export const isHumanAgentMessage = (message: GenesysMessagePayload): boolean => {
  if (message.direction !== 'Outbound') {
    return false;
  }

  if (!message.originatingEntity) {
    return !!message.channel?.from?.nickname || !!message.channel?.from?.image;
  }

  return message.originatingEntity === 'Human';
};

export const getAgentFirstName = (agentName?: string): string | undefined => {
  const trimmed = agentName?.trim();
  if (!trimmed || trimmed === GENESYS_FALLBACK_AGENT_NAME) {
    return undefined;
  }
  return trimmed.split(/\s+/)[0];
};
